import { useContext } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Chip,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import { ColorModeContext } from '../../contexts/ColorModeContext';
import { palette } from '../../theme/colors';
import {
  SubscriptionTier,
  TIER_NAMES,
  TIER_COLORS,
  TIER_FEATURES,
} from '../../types/subscription';

interface FeatureComparisonProps {
  /**
   * Current user's subscription tier
   * Used to highlight the current tier and show appropriate upgrade options
   */
  currentTier?: SubscriptionTier;
  /**
   * Callback when user clicks upgrade button for a tier
   * @param tier - The tier the user wants to upgrade to
   */
  onUpgrade?: (tier: SubscriptionTier) => void;
  /**
   * Whether to show upgrade buttons (default: true)
   */
  showUpgradeButtons?: boolean;
}

interface FeatureRow {
  label: string;
  getValue: (tier: SubscriptionTier) => string | boolean;
}

const FEATURE_ROWS: FeatureRow[] = [
  {
    label: 'Products',
    getValue: (tier) => TIER_FEATURES[tier].products,
  },
  {
    label: 'Locations',
    getValue: (tier) => TIER_FEATURES[tier].locations,
  },
  {
    label: 'Monthly Sales',
    getValue: (tier) => TIER_FEATURES[tier].monthlySales,
  },
  {
    label: 'B2B Customers',
    getValue: (tier) => TIER_FEATURES[tier].b2bCustomers,
  },
  {
    label: 'Advanced Analytics',
    getValue: (tier) => TIER_FEATURES[tier].advancedAnalytics,
  },
  {
    label: 'Report Exports',
    getValue: (tier) => TIER_FEATURES[tier].reportExports,
  },
  {
    label: 'Support Level',
    getValue: (tier) => TIER_FEATURES[tier].supportLevel,
  },
];

const TIER_PRICES: Record<SubscriptionTier, string> = {
  free: '$0',
  starter: '$29/mo',
  professional: '$99/mo',
  enterprise: '$299/mo',
};

/**
 * FeatureComparison Component
 *
 * Displays a comprehensive comparison table of all subscription tiers with their features and limits.
 * Shows pricing, feature availability (checkmarks/X marks), and upgrade CTAs.
 *
 * Features compared:
 * - Products limit (50 / 500 / 2000 / Unlimited)
 * - Locations limit (1 / 5 / 20 / Unlimited)
 * - Monthly sales limit (100 / 1000 / 5000 / Unlimited)
 * - B2B customers (✗ / ✗ / ✓ / ✓)
 * - Advanced analytics (✗ / ✗ / ✓ / ✓)
 * - Report exports (None / Basic / Advanced / Advanced)
 * - Support level (Community / Email / Priority / Dedicated)
 *
 * @example
 * ```tsx
 * <FeatureComparison
 *   currentTier="free"
 *   onUpgrade={(tier) => navigateTo(`/checkout/${tier}`)}
 * />
 * ```
 */
export const FeatureComparison = ({
  currentTier = 'free',
  onUpgrade,
  showUpgradeButtons = true,
}: FeatureComparisonProps) => {
  const { mode } = useContext(ColorModeContext);

  const colors = mode === 'dark' ? {
    surface: palette.surface,
    surfaceElevated: palette.surfaceElevated,
    border: palette.border,
    textPrimary: palette.textPrimary,
    textSecondary: palette.textSecondary,
    textMuted: palette.textMuted,
  } : {
    surface: palette.light.surface,
    surfaceElevated: palette.light.backgroundElevated,
    border: palette.light.border,
    textPrimary: palette.light.textPrimary,
    textSecondary: palette.light.textSecondary,
    textMuted: palette.light.textMuted,
  };

  const tiers: SubscriptionTier[] = ['free', 'starter', 'professional', 'enterprise'];

  const renderFeatureValue = (value: string | boolean) => {
    if (typeof value === 'boolean') {
      return value ? (
        <CheckCircleIcon
          sx={{
            color: palette.success,
            fontSize: 24,
          }}
          data-testid="check-icon"
        />
      ) : (
        <CancelIcon
          sx={{
            color: palette.textMuted,
            fontSize: 24,
            opacity: 0.5,
          }}
          data-testid="cancel-icon"
        />
      );
    }
    return (
      <Typography variant="body2" color="text.primary">
        {value}
      </Typography>
    );
  };

  const handleUpgrade = (tier: SubscriptionTier) => {
    if (onUpgrade) {
      onUpgrade(tier);
    } else {
      // Default behavior: navigate to checkout
      window.location.href = `/checkout/${tier}`;
    }
  };

  const isRecommended = (tier: SubscriptionTier) => tier === 'professional';

  return (
    <Box sx={{ width: '100%', overflowX: 'auto' }}>
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          bgcolor: colors.surface,
          border: `1px solid ${colors.border}`,
          borderRadius: 1,
        }}
      >
        <Table>
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  fontWeight: 'bold',
                  bgcolor: colors.surfaceElevated,
                  borderBottom: `2px solid ${colors.border}`,
                }}
              >
                <Typography variant="subtitle1" fontWeight="bold">
                  Features
                </Typography>
              </TableCell>
              {tiers.map((tier) => (
                <TableCell
                  key={tier}
                  align="center"
                  sx={{
                    bgcolor: isRecommended(tier)
                      ? `${TIER_COLORS[tier]}15`
                      : colors.surfaceElevated,
                    borderBottom: `2px solid ${colors.border}`,
                    borderLeft: `1px solid ${colors.border}`,
                    position: 'relative',
                    minWidth: 150,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 1,
                      py: 1,
                    }}
                  >
                    {isRecommended(tier) && (
                      <Chip
                        label="Recommended"
                        size="small"
                        sx={{
                          bgcolor: TIER_COLORS[tier],
                          color: '#FFFFFF',
                          fontWeight: 'bold',
                          fontSize: '0.7rem',
                        }}
                      />
                    )}
                    <Typography
                      variant="h6"
                      fontWeight="bold"
                      sx={{ color: TIER_COLORS[tier] }}
                    >
                      {TIER_NAMES[tier]}
                    </Typography>
                    <Typography variant="h5" fontWeight="bold">
                      {TIER_PRICES[tier]}
                    </Typography>
                    {currentTier === tier && (
                      <Chip
                        label="Current Plan"
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    )}
                  </Box>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {FEATURE_ROWS.map((feature, index) => (
              <TableRow
                key={feature.label}
                sx={{
                  '&:last-child td': { borderBottom: 0 },
                  bgcolor: index % 2 === 0 ? 'transparent' : `${colors.border}20`,
                }}
              >
                <TableCell>
                  <Typography variant="body2" fontWeight="medium">
                    {feature.label}
                  </Typography>
                </TableCell>
                {tiers.map((tier) => (
                  <TableCell
                    key={tier}
                    align="center"
                    sx={{
                      borderLeft: `1px solid ${colors.border}`,
                      bgcolor: isRecommended(tier)
                        ? `${TIER_COLORS[tier]}08`
                        : 'transparent',
                    }}
                  >
                    {renderFeatureValue(feature.getValue(tier))}
                  </TableCell>
                ))}
              </TableRow>
            ))}
            {showUpgradeButtons && (
              <TableRow>
                <TableCell
                  sx={{
                    bgcolor: colors.surfaceElevated,
                    borderTop: `2px solid ${colors.border}`,
                  }}
                >
                  <Typography variant="body2" fontWeight="bold">
                    Get Started
                  </Typography>
                </TableCell>
                {tiers.map((tier) => (
                  <TableCell
                    key={tier}
                    align="center"
                    sx={{
                      bgcolor: isRecommended(tier)
                        ? `${TIER_COLORS[tier]}15`
                        : colors.surfaceElevated,
                      borderTop: `2px solid ${colors.border}`,
                      borderLeft: `1px solid ${colors.border}`,
                      py: 2,
                    }}
                  >
                    {currentTier === tier ? (
                      <Typography variant="body2" color="text.secondary">
                        Current Plan
                      </Typography>
                    ) : tier === 'enterprise' ? (
                      <Button
                        variant={isRecommended(tier) ? 'contained' : 'outlined'}
                        color="primary"
                        fullWidth
                        onClick={() => handleUpgrade(tier)}
                      >
                        Contact Sales
                      </Button>
                    ) : (
                      <Button
                        variant={isRecommended(tier) ? 'contained' : 'outlined'}
                        color="primary"
                        fullWidth
                        startIcon={<RocketLaunchIcon />}
                        onClick={() => handleUpgrade(tier)}
                      >
                        Upgrade
                      </Button>
                    )}
                  </TableCell>
                ))}
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default FeatureComparison;
